// unified-catalogue.ts — one dataset, two readings: the unified catalogue view.
//
// This projection replaces the PV combinations view and the three separate catalogues (materials,
// combinations, supplier purchases). The xlsx workbook and the screen read the same function, so a
// printed figure and an on-screen figure cannot diverge. The combinations tab becomes a filter on
// `lineType` rather than a source.
//
// This module prices nothing: every value comes from an engine function already used by quotes.
// Everything is at tier 1, so no PV or PA shown here carries a volume, agency or client discount.

import { ACTIVE_CATALOGUE, type Catalogue } from "./catalogue-source";
import { pvCombi, pvCombiSimple, minimaCombi } from "./catalogue-pricing";
import { stdPrintRef } from "./base-price";
import { MAT_FORMATS, DEFAULT_MAT_FORMAT, SERVICE_KEY } from "../../data";
import { targetPaM2, paSupplierLookup, paMode, EMBEDDED_PA_GRIDS, type PaRateCards } from "../pricing/pa-margins";
import { lineWorkshopMenu } from "../workshops/workshops";
import { volumeCategory, type VolumeGrid } from "../pricing/volume-tiers";
import type { QuoteLine } from "../core/core";

/** One catalogue combination, as carried by `combinations.json`. */
export interface CombiSource {
  ref: string; material: string; print: string; cut: string;
  lamination?: string; finishing1?: string; finishing2?: string;
}

/** Unified-view line types — single source, never re-listed by hand. */
export const LINE_KINDS = ["standard", "specific"] as const;
export type CatalogueLineKind = (typeof LINE_KINDS)[number];

/** Opaque PA-coverage codes (UI labels via i18n) — three states, not a boolean. Single source for admin filters. */
export const PA_COVERAGES = ["collected", "elsewhere", "todo"] as const;
export type PaCoverage = (typeof PA_COVERAGES)[number];

/** Straight cuts — those that bill no minimum of their own. Enumerated, never guessed from a label. */
export const STRAIGHT_CUTS = ["CUT-1501", "CUT-1502"] as const;

/**
 * Is a line the standard combination of its material?
 * All three conditions are required, and the print is the one usually forgotten: a straight-cut
 * combination with no finishing looks standard, but if its print is not the material's standard
 * print it is not — and its minima will rightly be higher.
 * @param c The catalogue combination.
 * @param cat The catalogue.
 * @returns True when the combination is the material's standard one.
 */
export function isStandard(c: CombiSource, cat: Catalogue = ACTIVE_CATALOGUE): boolean {
  if (c.lamination || c.finishing1 || c.finishing2) return false;
  if (!(STRAIGHT_CUTS as readonly string[]).includes(c.cut)) return false;
  const materialEntry = cat.materials.find((m) => m.ref === c.material);
  return !!materialEntry && c.print === stdPrintRef(materialEntry);
}

/**
 * Expected relation between a row's minima and its material's (§3 invariant), stated once.
 *
 * On a STANDARD row the minimum flat fee and the minimum unit price EXACTLY equal the material's: the
 * components of a standard line (base print, straight cut) carry no minimum of their own. On a SPECIFIC
 * row they NEVER go below — a component never has a negative minimum — and rise as soon as a component
 * carries one (complex cut, lettering, mounting). "Specific" does NOT imply "strictly higher": many
 * lamination and finishing components carry no minimum, and that equality is right.
 */
export interface ExpectedMinima {
  equalsMaterial: boolean;
  neverLower: true;
}

/**
 * The minima invariant of a row kind.
 * @param type The row kind.
 * @returns Whether the minima must equal the material's, and that they never go below.
 */
export function expectedMinima(type: CatalogueLineKind): ExpectedMinima {
  return { equalsMaterial: type === "standard", neverLower: true };
}

/** A row of the unified view: the columns common to both readings. */
export interface UnifiedCatalogueRow {
  /** Ref of the combination, or of the material for standard rows which have no combination ref. */
  ref: string;
  lineType: CatalogueLineKind;
  material: string;
  materialName: string;
  category: string;
  print: string;
  cut: string;
  lamination: string;
  finishing1: string;
  finishing2: string;
  /** PV €/m² at tier 1, sum of components; never a discount. */
  pvM2: number | null;
  /** Effective minimum unit price, per piece. */
  minUnitPrice: number | null;
  /** Effective minimum flat fee (material plus component fees, consolidated once per type). */
  minFlatFee: number | null;
  /** The material's formats, inherited by combination rows. The default one is flagged. */
  formats: string[];
  defaultFormat: string;
  /** Derived purchase mode: a "U" twin is in in-house stock, so the material is supplied by us. */
  mode: "INCLUDED" | "SUPPLIED";
  /** Tier 1 target margin (fraction 0..1), read from the injected grid. */
  targetMargin: number | null;
  /** Target PA €/m² at tier 1. */
  targetPaM2: number | null;
  /** Cumul category of the volume tiers; this is what groups the m², not the display category. */
  volumeCategory: string | null;
  /** Rank-1 workshop of the family, the only reference for the derived columns. */
  workshop1: string;
  /** Tier 1 grid PA €/m² of the rank-1 workshop; `null` while the grid is not collected. */
  paGrid1M2: number | null;
  /** Grid PA per workshop, one entry each; `null` means the grid is not collected. */
  paGridByWorkshop: Readonly<Record<string, number | null>>;
  /** PV €/m² derived from the rank-1 workshop's grid. */
  pvGridM2: number | null;
  /** Percentage gap between the derived PV and the target PV. */
  gapPct: number | null;
  /** Real margin % (PV − rank-1 grid PA) ÷ PV; `null` without a grid, never a zero. */
  realMarginPct: number | null;
  /**
   * PA coverage in three states. A boolean would lie: counting a line covered as soon as any
   * workshop has a grid hides the lines whose grid belongs to a workshop we do not use, and where
   * the quote falls back to the target PA. Those get their own state.
   */
  paCoverage: PaCoverage;
}

/**
 * The workshops that can produce a catalogue line, derived from the real menus rather than listed
 * by hand, so the list follows the next collected grid on its own.
 * @param combis The catalogue combinations.
 * @returns The sorted workshop labels.
 */
export function catalogueWorkshops(combis: readonly CombiSource[]): string[] {
  const union = new Set<string>();
  for (const c of combis) lineWorkshopMenu(modelLine(c)).options.forEach((o) => union.add(o.label));
  return [...union].sort((a, b) => a.localeCompare(b, "fr"));
}

/**
 * Combinations priced somewhere, i.e. the ones the quote's combination-ref cell offers. "Priced"
 * means at least one workshop returns a real PA via `paSupplierLookup`, the same function the quote
 * uses, with its fallback rules. Both modes are probed, and the probed workshops are those the
 * grids actually carry. A hidden combination stays in the catalogue: we hide an input without a
 * known cost, we do not withdraw a product.
 */
const PROBE_GRID: VolumeGrid = { tiers: [{ sort_order: 0, minThreshold: 0, maxThreshold: null, discountRate: 0, targetMargin: 0.6 }] };

/**
 * Combinations priced by at least one workshop (see above).
 * @param combis The catalogue combinations.
 * @param paGrids The injected PA tables, if any (defaults to the embedded ones).
 * @returns The refs of the priced combinations.
 */
export function pricedCombinations(
  combis: readonly CombiSource[],
  paGrids?: PaRateCards,
): Set<string> {
  const grids = paGrids ?? EMBEDDED_PA_GRIDS;
  const keys = new Set(grids.map((e) => e.workshop));
  const labels = Object.entries(SERVICE_KEY).filter(([, k]) => keys.has(k)).map(([label]) => label);
  const priced = new Set<string>();
  const opts = { cumulatedM2: 0, grid: PROBE_GRID, ...(paGrids ? { paGrids } : {}) };
  for (const c of combis) {
    for (const label of labels) {
      const l = modelLine(c, label);
      if (paSupplierLookup(l, "INCLUDED", opts) || paSupplierLookup(l, "SUPPLIED", opts)) {
        priced.add(c.ref);
        break;
      }
    }
  }
  return priced;
}

/**
 * The synthetic line used to probe the quote functions. Its 1000 × 1000 dimensions only satisfy
 * the type: everything read here is €/m² at tier 1, so independent of area.
 * @param c The catalogue combination.
 * @param workshop The workshop to probe, if any.
 * @returns The synthetic line.
 */
function modelLine(c: CombiSource, workshop?: string): QuoteLine {
  return {
    type: "PRINT_GF", material: c.material, print: c.print, cut: c.cut,
    lamination: c.lamination || undefined, finishing1: c.finishing1 || undefined, finishing2: c.finishing2 || undefined,
    L: 1000, H: 1000, fileQty: 1, copiesPerFile: 1, workshopResp: workshop,
  } as QuoteLine;
}

/**
 * Real margin %, the one the supplier grid actually allows. `null` without a grid.
 * @param pvM2 The PV €/m².
 * @param gridPaM2 The supplier grid PA €/m².
 * @returns The real margin in percent, or null.
 */
export function realMarginPct(pvM2: number | null, gridPaM2: number | null): number | null {
  if (pvM2 == null || gridPaM2 == null || !(pvM2 > 0)) return null;
  return Math.round(((pvM2 - gridPaM2) / pvM2) * 1000) / 10;
}

/**
 * Purchase mode of a material, derived from the "ref + U" convention, never from an entered flag.
 * @param ref The material reference.
 * @param cat The catalogue.
 * @returns "SUPPLIED" for an in-house stock material, otherwise "INCLUDED".
 */
export function materialMode(ref: string, cat: Catalogue = ACTIVE_CATALOGUE): "INCLUDED" | "SUPPLIED" {
  const m = cat.materials.find((x) => x.ref === ref);
  return (m as { stockUpz?: boolean } | undefined)?.stockUpz ? "SUPPLIED" : "INCLUDED";
}

/**
 * Projects one row, the single entry point for both the workbook and the screen.
 * `null` when the material is unknown, rather than a half-filled row read as a reference.
 * @param c The catalogue combination.
 * @param grid The injected volume grid.
 * @param cat The catalogue.
 * @param workshops The workshops to read a grid PA for.
 * @param paGrids The injected PA tables, if any.
 * @returns The unified row, or null.
 */
export function projectRow(
  c: CombiSource,
  grid: VolumeGrid,
  cat: Catalogue = ACTIVE_CATALOGUE,
  workshops: readonly string[] = [],
  paGrids?: PaRateCards,
): UnifiedCatalogueRow | null {
  const materialEntry = cat.materials.find((m) => m.ref === c.material);
  if (!materialEntry) return null;
  const type: CatalogueLineKind = isStandard(c, cat) ? "standard" : "specific";
  const m = minimaCombi(c.ref, cat);
  // A standard row is priced by `pvCombiSimple`, a specific one by the sum of its real components.
  const pv = type === "standard" ? pvCombiSimple(c.material, cat) : pvCombi(c.ref, cat);
  const p1 = grid.tiers.find((p) => p.sort_order === 0) ?? grid.tiers[0];
  // Purchase side, read by the quote functions. The mode is derived from the material, since a UPZ
  // material only has a grid in SUPPLIED mode and probing the wrong side misses half the grids.
  const base = modelLine(c);
  const mode = paMode(base);
  const workshop1 = lineWorkshopMenu(base).rank1 ?? "";
  const lookOpts = {
    cumulatedM2: 0,
    grid,
    ...(paGrids ? { paGrids } : {}),
  };
  /**
   * Grid PA of a workshop for this combination.
   * @param workshop The workshop label.
   * @returns The grid PA €/m², or null when not collected.
   */
  const gridPaOf = (workshop: string): number | null =>
    workshop ? (paSupplierLookup(modelLine(c, workshop), mode, lookOpts)?.pa ?? null) : null;
  const byWorkshop: Record<string, number | null> = {};
  for (const w of workshops) byWorkshop[w] = gridPaOf(w);
  const paGrid1 = workshop1 ? (byWorkshop[workshop1] ?? gridPaOf(workshop1)) : null;
  const pvGrid = pvFromGrid(paGrid1, p1 ? p1.targetMargin : null);
  return {
    ref: c.ref,
    lineType: type,
    material: c.material,
    materialName: (materialEntry as { name?: string }).name ?? "",
    category: (materialEntry as { cat?: string }).cat ?? "",
    print: c.print,
    cut: c.cut,
    lamination: c.lamination ?? "",
    finishing1: c.finishing1 ?? "",
    finishing2: c.finishing2 ?? "",
    pvM2: pv,
    minUnitPrice: m?.minUnitPrice ?? null,
    minFlatFee: m?.minFlatFee ?? null,
    // Formats belong to the material; a combination inherits them rather than having its own.
    formats: (MAT_FORMATS as Record<string, string[]>)[c.material] ?? [],
    defaultFormat: (DEFAULT_MAT_FORMAT as Record<string, string>)[c.material] ?? "",
    mode: materialMode(c.material, cat),
    targetMargin: p1 ? p1.targetMargin : null,
    targetPaM2: pv != null && p1 ? targetPaM2(pv, p1.discountRate, p1.targetMargin) : null,
    volumeCategory: volumeCategory(base, cat),
    workshop1,
    paGrid1M2: paGrid1,
    paGridByWorkshop: byWorkshop,
    pvGridM2: pvGrid,
    gapPct: gridPvDelta(pvGrid, pv),
    realMarginPct: realMarginPct(pv, paGrid1),
    paCoverage: paGrid1 != null
      ? "collected"
      : workshops.some((w) => byWorkshop[w] != null) ? "elsewhere" : "todo",
  };
}

/**
 * PV €/m² derived from a supplier grid: grid PA ÷ (1 − target margin).
 * `null` without a grid PA, never 0, which would read as "no gap" instead of "unknown".
 * @param gridPaM2 The supplier grid PA €/m².
 * @param targetMargin The target margin (fraction).
 * @returns The derived PV €/m², or null.
 */
export function pvFromGrid(gridPaM2: number | null | undefined, targetMargin: number | null): number | null {
  if (gridPaM2 == null || targetMargin == null || targetMargin >= 1) return null;
  return Math.round((gridPaM2 / (1 - targetMargin)) * 100) / 100;
}

/**
 * Percentage gap between the grid-derived PV and the target PV. `null` if either is missing.
 * @param pvGrid The grid-derived PV €/m².
 * @param pvTarget The target PV €/m².
 * @returns The gap in percent, or null.
 */
export function gridPvDelta(pvGrid: number | null, pvTarget: number | null): number | null {
  if (pvGrid == null || pvTarget == null || !(pvTarget > 0)) return null;
  return Math.round((pvGrid / pvTarget - 1) * 1000) / 10;
}
